import { useRef, useCallback, useEffect, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import { HtmlNode } from "./HtmlNode";
import {
  FORMAT_TEXT_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  LexicalCommand,
} from "lexical";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  nodeKey: string;
  html: string;
}

// Map of Lexical FORMAT_TEXT_COMMAND format → execCommand equivalent
export const FORMAT_TO_EXEC: Record<string, string> = {
  bold: "bold",
  italic: "italic",
  underline: "underline",
  strikethrough: "strikeThrough",
  subscript: "subscript",
  superscript: "superscript",
  fontSize: "fontSize",
  backColor: "backColor",
  foreColor: "foreColor",
};

// ─── Hook: track whether selection is inside this signature ───────────────────

function useIsSelectionInside(ref: React.RefObject<HTMLDivElement>) {
  const [isInside, setIsInside] = useState(false);

  useEffect(() => {
    const check = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !ref.current) {
        setIsInside(false);
        return;
      }
      setIsInside(ref.current.contains(sel.anchorNode));
    };

    document.addEventListener("selectionchange", check);
    return () => document.removeEventListener("selectionchange", check);
  }, [ref]);

  return isInside;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HtmlDecorator({ nodeKey, html }: Props) {
  const [editor] = useLexicalComposerContext();
  const ref = useRef<HTMLDivElement>(null);
  const isInsideRef = useRef(false);
  const isSelectionInside = useIsSelectionInside(ref);

  // Keep a ref in sync so command handlers (which close over stale state) work
  useEffect(() => {
    isInsideRef.current = isSelectionInside;
  }, [isSelectionInside]);

  // Sync innerHTML back into SignatureNode on blur
  const syncToNode = useCallback(() => {
    const updatedHTML = ref.current?.innerHTML ?? "";
    editor.update(() => {
      const node = $getNodeByKey(nodeKey) as HtmlNode | null;
      if (node && updatedHTML !== node.__html) {
        node.setHTML(updatedHTML);
      }
    });
  }, [editor, nodeKey]);

  // ── Intercept Lexical toolbar commands when selection is inside ──────────────

  useEffect(() => {
    // Bold / Italic / Underline / Strikethrough etc.
    const unregisterFormat = editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      (format: string) => {
        if (!isInsideRef.current) return false; // let Lexical handle normally
        const execCmd = FORMAT_TO_EXEC[format];
        if (execCmd) document.execCommand(execCmd, false);
        syncToNode();
        return true; // consumed — don't let Lexical process it
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    return () => {
      unregisterFormat();
    };
  }, [editor, syncToNode]);

  // ── Listen for custom style commands from toolbar (color, font, size) ────────
  // Your toolbar should dispatch these custom events on the document when
  // a style button is clicked. See ToolbarBridge.tsx below.

  useEffect(() => {
    const handleStyleCommand = (e: Event) => {
      if (!isInsideRef.current) return;
      const { command, value } = (e as CustomEvent).detail as {
        command: string;
        value: string;
      };
      document.execCommand(command, false, value);
      syncToNode();
    };

    document.addEventListener("signature:style", handleStyleCommand);
    return () =>
      document.removeEventListener("signature:style", handleStyleCommand);
  }, [syncToNode]);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      dangerouslySetInnerHTML={{ __html: html }}
      onBlur={syncToNode}
      onInput={stop}
      onKeyDown={stop}
      onKeyUp={stop}
      onKeyPress={stop}
      onPaste={stop}
      onCut={stop}
      onCopy={stop}
      onClick={stop}
      onMouseDown={stop}
      data-signature="true"
      style={{ outline: "none", cursor: "text", borderRadius: 4 }}
      onFocus={(e) => {
        // e.currentTarget.style.outline = "2px solid rgba(59,130,246,0.25)";
      }}
      onBlurCapture={(e) => {
        e.currentTarget.style.outline = "none";
      }}
    />
  );
}
